import React, { useLayoutEffect, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { handleSidebarAbsolute, handleSidebarOpen, handleSidebarWidth } from 'store/appSlice';
import { RootState } from 'store';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { ArrowLeftIcon } from 'components/icons/ArrowLeftIcon';
import { TrashDeleteBinIcon } from 'components/icons/TrashDeleteBinIcon';
import { DownloadIcon } from 'components/icons/DownloadIcon';
import { ProfilePicIcon } from 'components/icons/ProfilePicIcon';
import { FolderIcon } from 'components/icons/FolderIcon';
import { SurveyIcon } from 'components/icons/SurveyIcon';
import { YoutubeIcon } from 'components/icons/YoutubeIcon';
import { CaptionsIcon } from 'components/icons/CaptionsIcon';
import { TranscriptIcon } from 'components/icons/TranscriptIcon';
import { BarChartIcon } from 'components/icons/BarChartIcon';
import { FilmEditIcon } from 'components/icons/FilmEditIcon';
import { MessagesIcon } from 'components/icons/MessagesIcon';
import { SquareInfoIcon } from 'components/icons/SquareInfoIcon';
import { Share2Icon } from 'components/icons/ShareIcon2';
import { ImageIcon } from 'components/icons/ImageIcon';

const scrollContainer = {
  maxHeight: 'calc(100vh - 100px)'
};

function StudioSidebar() {
  const location = useLocation();
  const dispatch = useDispatch();
  const app = useSelector((state: RootState) => state.app);
  const { sidebarOpen, secondaryViewWidth, secondaryViewOpen, sidebarWidth, sidebarAbsolute } = app;

  const { pathname } = location;
  const { videoId, workspaceId } = useParams();
  const isVideoPage = pathname?.match('/videos/') ? true : false;
  const sidebar = useRef(document.createElement('div'));

  const [isResizing, setIsResizing] = useState(false);
  const [manualResize, setManualResize] = useState(false);
  const [displayResizer] = useState(true);
  const navigate = useNavigate();
  // const [displayResizer, handleDisplayResizer] = useState(true);

  function updateSidebarStates() {
    const sidebarDom = document.getElementById('sidebar');
    if (sidebarDom) {
      const sidebarWidth = sidebarDom.clientWidth;
      if (!secondaryViewOpen && !isVideoPage) {
        if (window.innerWidth - sidebarWidth < 963) {
          // handleDisplayResizer(false);
          dispatch(handleSidebarOpen(false));
          dispatch(handleSidebarAbsolute(true));
        } else {
          // handleDisplayResizer(true);
          dispatch(handleSidebarOpen(true));
          dispatch(handleSidebarAbsolute(false));
        }
      }
    }
  }

  useEffect(() => {
    if (secondaryViewOpen) {
      if (window.innerWidth - secondaryViewWidth - sidebarWidth < 500) {
        dispatch(handleSidebarAbsolute(true));
      } else {
        if (sidebarOpen) {
          dispatch(handleSidebarAbsolute(false));
        } else {
          dispatch(handleSidebarAbsolute(true));
        }
      }
    } else {
      if (window.innerWidth - sidebarWidth < 385) {
        dispatch(handleSidebarAbsolute(true));
      } else {
        if (sidebarOpen) {
          dispatch(handleSidebarAbsolute(false));
        } else {
          dispatch(handleSidebarAbsolute(true));
        }
      }
    }
  }, [sidebarOpen]);

  useLayoutEffect(() => {
    window.addEventListener('resize', updateSidebarStates);
    updateSidebarStates();
    return () => window.removeEventListener('resize', updateSidebarStates);
  }, []);

  const startResizing = React.useCallback(() => {
    setManualResize(true);
    setIsResizing(true);
  }, []);

  const stopResizing = React.useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = React.useCallback(
    (mouseMoveEvent) => {
      if (isResizing && !isVideoPage) {
        if (manualResize) {
          if (secondaryViewOpen) {
            if (mouseMoveEvent.clientX < 140) {
              dispatch(handleSidebarOpen(false));
              dispatch(handleSidebarWidth(180));
            } else {
              dispatch(handleSidebarOpen(true));
              if (window.innerWidth - mouseMoveEvent.clientX - secondaryViewWidth > 500) {
                dispatch(handleSidebarWidth(mouseMoveEvent.clientX));
              } else {
                dispatch(handleSidebarWidth(window.innerWidth - 500 - secondaryViewWidth));
              }
            }
          } else {
            if (mouseMoveEvent.clientX < 140) {
              dispatch(handleSidebarOpen(false));
              dispatch(handleSidebarWidth(180));
            } else if (mouseMoveEvent.clientX > 600) {
              dispatch(handleSidebarWidth(600));
            } else {
              dispatch(handleSidebarOpen(true));
              dispatch(handleSidebarWidth(mouseMoveEvent.clientX));
            }
          }
        }
      }
    },
    [isResizing]
  );

  useEffect(() => {
    if (!isVideoPage) {
      if (secondaryViewOpen) {
        if (window.innerWidth - secondaryViewWidth - sidebarWidth < 500) {
          dispatch(handleSidebarOpen(false));
          dispatch(handleSidebarAbsolute(true));
          // setSidebarWidth(180);
        } else {
          dispatch(handleSidebarOpen(true));
          dispatch(handleSidebarAbsolute(false));
        }
      } else {
        if (window.innerWidth - sidebarWidth < 385) {
          dispatch(handleSidebarAbsolute(true));
        } else {
          if (sidebarOpen) {
            dispatch(handleSidebarAbsolute(false));
          } else {
            dispatch(handleSidebarAbsolute(true));
          }
        }
      }
    }
  }, [secondaryViewWidth]);

  useEffect(() => {
    window.addEventListener('mousemove', resize);
    window.addEventListener('mouseup', stopResizing);
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [resize, stopResizing]);

  useEffect(() => {
    const keyHandler = ({ keyCode }: any) => {
      if (!sidebarOpen || keyCode !== 27) return;
      dispatch(handleSidebarOpen(false));
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  const handleDblClick = (e: any) => {
    if (e.detail === 2) {
      dispatch(handleSidebarWidth(200));
      setManualResize(false);
    }
  };

  const addPath = (to: string) => {
    return `/workspace/${workspaceId}/studio/${videoId}/${to}`;
  };

  return (
    <div className="font-lato z-20">
      <div
        className={`fixed inset-0 bg-transparent z-40 lg:z-auto transition-opacity  ${
          sidebarOpen && sidebarAbsolute ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden="true"
        onClick={() => dispatch(handleSidebarOpen(false))}
      ></div>
      <div
        id="sidebar"
        ref={sidebar}
        style={{
          width: manualResize ? sidebarWidth : 'none',
          transform: sidebarOpen ? 'translate(0px)' : `translate(-${sidebarWidth + 60}px)`,
          position: sidebarAbsolute ? 'absolute' : 'static'
        }}
        className={clsx(
          'border-t border-gray-border app-sidebar flex flex-col z-40 left-0 top-11 md:left-auto md:top-auto transform h-[calc(100vh-44px)] shrink-0 bg-gray-sidebar transition-all duration-0 ease-in-out overflow-visible 2xl:flex-1 ',
          {
            'min-w-[180px] md:min-w-[180px] lg:min-w-[180px] xl:min-w-[180px]': manualResize,
            'min-w-[200px] md:min-w-[200px] lg:min-w-[220px] xl:min-w-[260px]': !manualResize
          }
        )}
        onMouseDown={(e) => e.preventDefault()}
      >
        <div className="app-sidebar-content">
          <div>
            <div
              className="flex px-4 font-black text-white leading-6 h-[50px] items-center cursor-pointer hover:bg-white hover:bg-opacity-10 border-b border-gray-border"
              onClick={() => navigate('/videos/all')}
            >
              <ArrowLeftIcon className="w-6 h-6  stroke-white" />
              <span className="text-[17px] ml-2">Workspace</span>
            </div>
            <PerfectScrollbar component="div" style={scrollContainer} options={{ suppressScrollY: false }}>
              <div>
                <ul className="pt-3">
                  <li
                    className={`px-4 last:mb-0 group ${
                      pathname === `/workspace/${workspaceId}/studio/${videoId}/video-details`
                        ? 'bg-blue-active text-white'
                        : 'hover:bg-gray-highlight'
                    }`}
                  >
                    <NavLink
                      end
                      to={addPath('video-details')}
                      className={`block text-white font-normal text-opacity-70 transition ${
                        pathname === `/workspace/${workspaceId}/studio/${videoId}/video-details`
                          ? 'font-bold text-opacity-100'
                          : ''
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="w-[26px]">
                          <SquareInfoIcon className={`w-[18px] h-[18px] stroke-white`} />
                        </span>
                        <span className="text-[15px] leading-[28px] truncate">Video details</span>
                      </div>
                    </NavLink>
                  </li>
                  <li
                    className={`px-4 last:mb-0 group ${
                      pathname === `/workspace/${workspaceId}/studio/${videoId}/external-share`
                        ? 'bg-blue-active text-white'
                        : 'hover:bg-gray-highlight'
                    }`}
                  >
                    <NavLink
                      end
                      to={addPath('external-share')}
                      className={`block text-white font-normal text-opacity-70 transition ${
                        pathname === `/workspace/${workspaceId}/studio/${videoId}/external-share`
                          ? 'font-bold text-opacity-100'
                          : ''
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="w-[26px]">
                          <Share2Icon className={`w-[18px] h-[18px] stroke-white`} />
                        </span>
                        <span className="text-[15px] leading-[28px] truncate">External sharing link</span>
                      </div>
                    </NavLink>
                  </li>
                  {/* <li
                    className={`px-4 last:mb-0 group ${
                      pathname.includes(`/studio/${videoId}/download`)
                        ? 'bg-blue-active text-white'
                        : 'hover:bg-gray-highlight'
                    }`}
                  >
                    <NavLink
                      end
                      to={`/studio/${videoId}/download`}
                      className={`block text-white text-opacity-70 ${
                        pathname.includes(`/studio/${videoId}/download`) ? 'font-bold text-opacity-100' : ''
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="w-[26px]">
                          <DownloadIcon className={`w-[18px] h-[18px] fill-white`} />
                        </span>
                        <span className="text-[15px] font-normal leading-[28px] truncate">Download</span>
                      </div>
                    </NavLink>
                  </li> */}
                  <li
                    className={`px-4 last:mb-0 group ${
                      pathname.includes(`/workspace/${workspaceId}/studio/${videoId}/speaker-info`)
                        ? 'bg-blue-active text-white'
                        : 'hover:bg-gray-highlight'
                    }`}
                  >
                    <NavLink
                      end
                      to={addPath('speaker-info')}
                      className={`block text-white text-opacity-70 ${
                        pathname.includes(`/workspace/${workspaceId}/studio/${videoId}/speaker-info`)
                          ? 'font-bold text-opacity-100'
                          : ''
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="w-[26px]">
                          <ProfilePicIcon className={`w-[18px] h-[18px] fill-white`} />
                        </span>
                        <span className="text-[15px] font-normal leading-[28px] truncate">Speaker info</span>
                      </div>
                    </NavLink>
                  </li>
                  <li
                    className={`px-4 last:mb-0 group ${
                      pathname.includes(`/workspace/${workspaceId}/studio/${videoId}/resources`)
                        ? 'bg-blue-active text-white'
                        : 'hover:bg-gray-highlight'
                    }`}
                  >
                    <NavLink
                      end
                      to={addPath('resources')}
                      className={`block text-white text-opacity-70 ${
                        pathname.includes(`/workspace/${workspaceId}/studio/${videoId}/resources`)
                          ? 'font-bold text-opacity-100'
                          : ''
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="w-[26px]">
                          <FolderIcon className={`w-[18px] h-[18px] fill-white`} />
                        </span>
                        <span className="text-[15px] font-normal leading-[28px] truncate">Resources</span>
                      </div>
                    </NavLink>
                  </li>
                  <li
                    className={`px-4 last:mb-0 group ${
                      pathname.includes(`/workspace/${workspaceId}/studio/${videoId}/polls`)
                        ? 'bg-blue-active text-white'
                        : 'hover:bg-gray-highlight'
                    }`}
                  >
                    <NavLink
                      end
                      to={addPath('polls')}
                      className={`block text-white text-opacity-70 ${
                        pathname.includes(`/workspace/${workspaceId}/studio/${videoId}/polls`)
                          ? 'font-bold text-opacity-100'
                          : ''
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="w-[26px]">
                          <SurveyIcon className={`w-[18px] h-[18px] fill-white`} />
                        </span>
                        <span className="text-[15px] font-normal leading-[28px] truncate">Polls</span>
                      </div>
                    </NavLink>
                  </li>
                  {/* <li
                    className={`px-4 last:mb-0 group ${
                      pathname.includes(`/studio/${videoId}/q&a`)
                        ? 'bg-blue-active text-white'
                        : 'hover:bg-gray-highlight'
                    }`}
                  >
                    <NavLink
                      end
                      to={`/studio/${videoId}/q&a`}
                      className={`block text-white text-opacity-70 ${
                        pathname.includes(`/studio/${videoId}/q&a`) ? 'font-bold text-opacity-100' : ''
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="w-[26px]">
                          <MessagesIcon className={`w-[18px] h-[18px] fill-white`} />
                        </span>
                        <span className="text-[15px] font-normal leading-[28px] truncate">Questions</span>
                      </div>
                    </NavLink>
                  </li> */}
                  {/* <li
                    className={`px-4 last:mb-0 group ${
                      pathname.includes(`/studio/${videoId}/source-videos`)
                        ? 'bg-blue-active text-white'
                        : 'hover:bg-gray-highlight'
                    }`}
                  >
                    <NavLink
                      end
                      to={`/studio/${videoId}/source-videos`}
                      className={`block text-white text-opacity-70 ${
                        pathname.includes(`/studio/${videoId}/source-videos`) ? 'font-bold text-opacity-100' : ''
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="w-[26px]">
                          <YoutubeIcon
                            className={`w-[18px] h-[18px] ${
                              pathname === `/studio/${videoId}/source-videos`
                                ? 'stroke-white text-white'
                                : 'stroke-black'
                            } group-hover:stroke-white`}
                          />
                        </span>
                        <span className="text-[15px] font-normal leading-[28px] truncate">Source videos</span>
                      </div>
                    </NavLink>
                  </li>
                  <li
                    className={`px-4 last:mb-0 group ${
                      pathname.includes(`/studio/${videoId}/caption`)
                        ? 'bg-blue-active text-white'
                        : 'hover:bg-gray-highlight'
                    }`}
                  >
                    <NavLink
                      end
                      to={`/studio/${videoId}/caption`}
                      className={`block text-white text-opacity-70 ${
                        pathname.includes(`/studio/${videoId}/caption`) ? 'font-bold text-opacity-100' : ''
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="w-[26px]">
                          <CaptionsIcon className={`w-[18px] h-[18px] fill-white`} />
                        </span>
                        <span className="text-[15px] font-normal leading-[28px] truncate">Add SRT/closed caption</span>
                      </div>
                    </NavLink>
                  </li>
                  <li
                    className={`px-4 last:mb-0 group ${
                      pathname.includes(`studio/${videoId}/transcript`)
                        ? 'bg-blue-active text-white'
                        : 'hover:bg-gray-highlight'
                    }`}
                  >
                    <NavLink
                      end
                      to={`studio/${videoId}/transcript`}
                      className={`block text-white text-opacity-70 ${
                        pathname.includes(`studio/${videoId}/transcript`) ? 'font-bold text-opacity-100' : ''
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="w-[26px]">
                          <TranscriptIcon className={`w-[18px] h-[18px] fill-white`} />
                        </span>
                        <span className="text-[15px] font-normal leading-[28px] truncate">Upload transcript file</span>
                      </div>
                    </NavLink>
                  </li> */}
                  <li
                    className={`px-4 last:mb-0 group ${
                      pathname.includes(`/workspace/${workspaceId}/studio/${videoId}/analytics`)
                        ? 'bg-blue-active text-white'
                        : 'hover:bg-gray-highlight'
                    }`}
                  >
                    <NavLink
                      end
                      to={addPath('analytics')}
                      className={`block text-white text-opacity-70 ${
                        pathname.includes(`/workspace/${workspaceId}/studio/${videoId}/analytics`)
                          ? 'font-bold text-opacity-100'
                          : ''
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="w-[26px]">
                          <BarChartIcon className={`w-[18px] h-[18px] fill-white`} />
                        </span>
                        <span className="text-[15px] font-normal leading-[28px] truncate">Analytics</span>
                      </div>
                    </NavLink>
                  </li>
                  <li
                    className={`px-4 last:mb-0 group ${
                      pathname.includes(`/workspace/${workspaceId}/studio/${videoId}/edit-mix`)
                        ? 'bg-blue-active text-white'
                        : 'hover:bg-gray-highlight'
                    }`}
                  >
                    <NavLink
                      end
                      to={addPath('edit-mix')}
                      className={`block text-white text-opacity-70 ${
                        pathname.includes(`/workspace/${workspaceId}/studio/${videoId}/edit-mix`)
                          ? 'font-bold text-opacity-100'
                          : ''
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="w-[26px]">
                          <FilmEditIcon className={`w-[18px] h-[18px] fill-white`} />
                        </span>
                        <span className="text-[15px] font-normal leading-[28px] truncate">Edit and mix</span>
                      </div>
                    </NavLink>
                  </li>
                  <li
                    className={`px-4 last:mb-0 group ${
                      pathname.includes(`/workspace/${workspaceId}/studio/${videoId}/bg-mix`)
                        ? 'bg-blue-active text-white'
                        : 'hover:bg-gray-highlight'
                    }`}
                  >
                    <NavLink
                      end
                      to={addPath('bg-mix')}
                      className={`block text-white text-opacity-70 ${
                        pathname.includes(`/workspace/${workspaceId}/studio/${videoId}/bg-mix`)
                          ? 'font-bold text-opacity-100'
                          : ''
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="w-[26px]">
                          <ImageIcon className={`w-[18px] h-[18px] fill-white`} />
                        </span>
                        <span className="text-[15px] font-normal leading-[28px] truncate">
                          Background image for mix
                        </span>
                      </div>
                    </NavLink>
                  </li>
                  <li
                    className={`px-4 rounded-sm last:mb-0 ${
                      pathname.includes(`/workspace/${workspaceId}/studio/${videoId}/delete`)
                        ? 'bg-blue-active text-white'
                        : 'hover:bg-gray-highlight'
                    }`}
                  >
                    <NavLink
                      end
                      to={addPath('delete')}
                      className={`block text-white text-opacity-70 ${
                        pathname.includes(`/workspace/${workspaceId}/studio/${videoId}/delete`)
                          ? 'font-bold text-opacity-100'
                          : ''
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="w-[26px]">
                          <TrashDeleteBinIcon className={`w-[18px] h-[18px] stroke-white`} />
                        </span>
                        <span className="text-[15px] font-normal leading-[28px] truncate">Delete</span>
                      </div>
                    </NavLink>
                  </li>
                </ul>
              </div>
            </PerfectScrollbar>
          </div>
        </div>
        <div
          className={clsx('app-sidebar-resizer', {
            block: displayResizer,
            hidden: !displayResizer
          })}
          style={{ left: sidebarWidth - 4 }}
          onMouseDown={startResizing}
          onClick={handleDblClick}
        />
      </div>
    </div>
  );
}

export default StudioSidebar;
